import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  lookupsApi, shippingApi, usersApi, type ShippingKind, type ShippingRequest,
} from '@/lib/apiClient';
import { Button } from '@/components';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { DATE_INPUT_MAX, DATE_INPUT_MIN } from '@/lib/dates';
import {
  KINDS, KIND_LABELS, buildCreateBody, buildUpdateBody, failureMessage, initialForm, jobLabel, problems, replaceRow, withoutRow,
  type ShippingChange, type ShippingForm, type ShippingPrefill,
} from './shippingForm';
import { ShippingStyles } from './ShippingStyles';
import { useShippingProjects, type ShippingProjectOption } from './useShippingProjects';

export type ShippingProjectRef = ShippingProjectOption;

/**
 * The new / edit drawer for a shipping request (#717, #718, #719). Self-contained: it reads its own lookups and
 * clears the lists and counts it makes stale, so any screen can open it with just the request (or none).
 *
 * A NEW request asks for everything Logistics otherwise phones to get (dimensions, weight, truck size, whether the
 * jobsite can receive it, an onsite contact) up front, so the first message already has them.
 *
 * What a request is FOR (its kind, its job, its material) is set when it is raised and is not editable: a request
 * filed against the wrong job is removed and raised again, which keeps the job-visibility check on one write path.
 */
export default function ShippingDrawer({ item, lockedProject, prefill, readOnly: readOnlyByHost = false, onClose, onSaved }: {
  /** null = a new request. */
  item: ShippingRequest | null;
  /** A new request pinned to this job (the job's tab, or a Materials row). Without it the job is chosen, or none. */
  lockedProject?: ShippingProjectRef;
  prefill?: ShippingPrefill;
  /**
   * Someone who can see a request but not change it, or a job whose activity is locked. (A request the server says THIS
   * caller may not change opens read-only by itself: see `item.canEdit`.)
   */
  readOnly?: boolean;
  onClose: () => void;
  onSaved: (change: ShippingChange) => void;
}) {
  const qc = useQueryClient();
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });
  const isNew = item === null;
  // A PM reads every request but changes only those on their own jobs; the server says which (`canEdit`) and refuses the
  // rest. Absent from an older API it reads as editable, and the write is still decided by the server.
  const notYours = item !== null && item.canEdit === false;
  const readOnly = readOnlyByHost || notYours;
  // Said only when it is the reason: a host that already made the drawer read-only (no permission, a locked job) needs no note.
  const whyReadOnly = notYours && !readOnlyByHost
    ? (item.projectId != null
        ? 'This request is on a job you are not assigned to, so Logistics changes it. You can read it here.'
        : 'You did not raise this request, so Logistics changes it. You can read it here.')
    : null;

  const [form, setForm] = useState<ShippingForm>(() => initialForm(item, prefill));
  const [projectId, setProjectId] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const { data: truckTypes } = useQuery({ queryKey: ['shipping-truck-types'], queryFn: () => lookupsApi.listShippingTruckTypes(), staleTime: 30 * 60 * 1000 });
  const { data: statuses } = useQuery({
    queryKey: ['shipping-statuses'], queryFn: () => lookupsApi.listShippingStatuses(), staleTime: 30 * 60 * 1000,
    enabled: !isNew,
  });

  // The job picker is only for a new request that is not already pinned to a job (see useShippingProjects for
  // why it is the visibility-scoped list).
  const needsPicker = isNew && !lockedProject;
  const { data: projectList } = useShippingProjects(needsPicker);

  const hasJob = isNew ? (lockedProject != null || projectId !== '') : item.projectId != null;
  const set = <K extends keyof ShippingForm>(k: K, v: ShippingForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const text = (k: 'items' | 'fromLocation' | 'toLocation' | 'dimensions' | 'weight' | 'contactName' | 'contactPhone' | 'notes' | 'neededByDate') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(k, e.target.value);

  /**
   * Show the change at ONCE, then refetch. Without the patch the list kept the old row for the length of the round trip
   * (a couple of seconds on a slow database), which reads as a save that did not take. The refetch still runs, so the row
   * lands where the list's order puts it (Expedited and open first) and the tab count follows.
   */
  const clearStale = (patch?: (rows: ShippingRequest[]) => ShippingRequest[]) => {
    if (patch) qc.setQueriesData<ShippingRequest[]>({ queryKey: ['shipping'] }, (old) => (Array.isArray(old) ? patch(old) : old));
    qc.invalidateQueries({ queryKey: ['shipping'] });
    qc.invalidateQueries({ queryKey: ['project-counts'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (item) {
        const body = buildUpdateBody(form, item);
        // Nothing changed: send nothing, so the row (and its updated_at) is left as it was.
        if (Object.keys(body).length === 0) return null;
        return shippingApi.update(item.id, body);
      }
      const chosen = lockedProject?.id ?? (projectId ? Number(projectId) : null);
      return shippingApi.create(buildCreateBody(form, { projectId: chosen, materialId: prefill?.materialId }));
    },
    onSuccess: (saved) => {
      if (saved === null) { onClose(); return; }
      // A new request has no row to patch: the refetch brings it.
      clearStale(isNew ? undefined : (rows) => replaceRow(rows, saved));
      onSaved(isNew ? 'created' : 'updated');
    },
    onError: (err) => setErrors([failureMessage(err)]),
  });

  const remove = useMutation({
    mutationFn: () => shippingApi.delete(item!.id),
    onSuccess: () => { clearStale((rows) => withoutRow(rows, item!.id)); onSaved('deleted'); },
    onError: (err) => setErrors([failureMessage(err)]),
  });

  const submit = () => {
    const found = problems(form, hasJob, isNew);
    setErrors(found);
    if (found.length === 0) save.mutate();
  };

  const busy = save.isPending || remove.isPending;
  const requestor = item ? (item.requestorName ?? '—') : (me?.displayName ?? 'You');
  const jobChip = useMemo(() => {
    if (item) return jobLabel(item);
    // The host passes empty strings until the job's own record has loaded; "# — " would read as a broken line.
    if (lockedProject) return lockedProject.jobNumber ? `#${lockedProject.jobNumber} — ${lockedProject.title}` : 'This job';
    return null;
  }, [item, lockedProject]);
  const title = item ? `${KIND_LABELS[item.kind] ?? 'Shipping'} request` : 'New shipping request';
  const showExpeditedNote = !isNew && !item.expedited;

  return (
    <>
      <ShippingStyles />
      <div className="shp-scrim" onClick={onClose} />
      <div ref={drawerRef} className="shp-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="shp-drawer-hd">
          <div>
            <div className="shp-drawer-title">{title}</div>
            <div className="shp-muted shp-small">Requestor: {requestor}</div>
          </div>
          <div className="shp-bar">
            {!isNew && !readOnly && (
              <Button variant="danger" size="sm" disabled={busy}
                onClick={() => { if (window.confirm('Delete this request?')) remove.mutate(); }}>
                Delete
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
            {!readOnly && (
              <Button size="sm" onClick={submit} isLoading={save.isPending} disabled={busy}>
                {isNew ? 'Submit request' : 'Save'}
              </Button>
            )}
          </div>
        </div>

        {whyReadOnly && <div className="shp-muted shp-small" role="note" style={{ margin: '8px 18px 0' }}>{whyReadOnly}</div>}

        {errors.length > 0 && (
          <div className="shp-error" role="alert" style={{ margin: '8px 18px 0' }}>
            {errors.map((m) => <div key={m}>{m}</div>)}
          </div>
        )}

        <fieldset className="shp-drawer-body" disabled={readOnly || busy}>
          <div className="shp-section">Request</div>
          <div className="shp-field">
            <span>Type</span>
            {isNew ? (
              <div className="shp-seg" role="radiogroup" aria-label="Type">
                {KINDS.map((k: ShippingKind) => (
                  <label key={k} className={form.kind === k ? 'on' : ''}>
                    <input type="radio" name="shipping-kind" value={k} checked={form.kind === k} onChange={() => set('kind', k)} />
                    {KIND_LABELS[k]}
                  </label>
                ))}
              </div>
            ) : (
              <div className="shp-job">{KIND_LABELS[item.kind] ?? item.kind}</div>
            )}
            <span className="shp-hint">Pickup: Logistics collects it. Delivery: Logistics brings it to a job or to the shop.</span>
          </div>

          <label className="shp-field">
            <span>What is being moved</span>
            <textarea value={form.items} onChange={text('items')} placeholder="e.g. Two 12 ft steel beams and the anchor bolts" maxLength={2000} />
          </label>

          <div className="shp-field">
            <span>Job{needsPicker ? ' (optional)' : ''}</span>
            {needsPicker ? (
              <select aria-label="Job" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">No job (excess material, vendor pickup…)</option>
                {(projectList ?? []).map((p) => <option key={p.id} value={p.id}>#{p.jobNumber} — {p.title}</option>)}
              </select>
            ) : (
              <div className="shp-job">{jobChip ?? 'No job'}</div>
            )}
          </div>

          <div className="shp-grid">
            <label className="shp-field">
              <span>From</span>
              <input value={form.fromLocation} onChange={text('fromLocation')} placeholder="Vendor, fab shop, warehouse, jobsite" maxLength={300} />
            </label>
            <label className="shp-field">
              <span>To</span>
              <input value={form.toLocation} onChange={text('toLocation')} placeholder="Jobsite, shop, warehouse" maxLength={300} />
            </label>
            <label className="shp-field">
              <span>Needed by</span>
              <input type="date" value={form.neededByDate} onChange={text('neededByDate')} min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} />
            </label>
            {!isNew && (
              <label className="shp-field">
                <span>Status</span>
                <select value={form.statusId} onChange={(e) => set('statusId', e.target.value)}>
                  {(statuses ?? []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
            )}
          </div>

          <label className="shp-field">
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={form.expedited} onChange={(e) => set('expedited', e.target.checked)} />
              Expedited
            </span>
            <span className="shp-hint">
              Handled as quickly as possible, and usually not before the next day. It puts the request first in Logistics’ list.
              {showExpeditedNote && ' Logistics is told when you mark a request Expedited.'}
            </span>
          </label>

          <div className="shp-section">What Logistics needs to know</div>
          <div className="shp-grid">
            <label className="shp-field">
              <span>Dimensions</span>
              <input value={form.dimensions} onChange={text('dimensions')} placeholder="e.g. 8 ft x 4 ft x 3 ft" maxLength={200} />
            </label>
            <label className="shp-field">
              <span>Weight</span>
              <input value={form.weight} onChange={text('weight')} placeholder="e.g. 2,400 lb" maxLength={100} />
            </label>
            <label className="shp-field">
              <span>Truck</span>
              <select value={form.truckTypeId} onChange={(e) => set('truckTypeId', e.target.value)}>
                <option value="">—</option>
                {(truckTypes ?? []).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <label className="shp-field">
              <span>Jobsite can receive it</span>
              <select value={form.siteReady} onChange={(e) => set('siteReady', e.target.value as ShippingForm['siteReady'])}>
                <option value="">—</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="shp-field">
              <span>Onsite contact</span>
              <input value={form.contactName} onChange={text('contactName')} maxLength={200} />
            </label>
            <label className="shp-field">
              <span>Contact phone</span>
              <input type="tel" value={form.contactPhone} onChange={text('contactPhone')} maxLength={50} />
            </label>
          </div>
          <span className="shp-hint">A delivery to a job needs the onsite contact, their phone number, and whether the jobsite can receive it.</span>

          <div className="shp-section">Notes</div>
          <label className="shp-field">
            <span className="shp-hint">Anything else Logistics should know.</span>
            <textarea aria-label="Notes" value={form.notes} onChange={text('notes')} maxLength={4000} />
          </label>
        </fieldset>
      </div>
    </>
  );
}
